/**
 * React Query hooks for fund distributions
 *
 * Provides query and mutation hooks for distribution management.
 */

import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { APIError } from '@/utils/api-client';
import {
  distributionService,
  type DistributionListResponse,
  type DistributionCreateRequest,
  type DistributionUpdateRequest,
  type DistributionFinalizeResponse,
  type DistributionNotice,
  type AvailableAssetSale,
} from '@/services/distributionService';
import type {
  Distribution,
  WaterfallCalculationResponse,
} from '@/types/distribution';
import type { FundTransactionDetail } from '@/types/fund-transactions';

// Query hooks

/**
 * Hook to fetch paginated distributions for a fund
 */
export const useFundDistributions = (
  fundId: string | undefined,
  params?: {
    page?: number;
    page_size?: number;
    status?: string;
    search?: string;
  }
) => {
  return useApiQuery<DistributionListResponse>(
    [...queryKeys.distributions.list(fundId!), params],
    () => distributionService.getDistributions(fundId!, params),
    {
      enabled: !!fundId,
      staleTime: 30 * 1000, // 30 seconds
    }
  );
};

/**
 * Hook to fetch a single distribution
 */
export const useDistribution = (
  fundId: string | undefined,
  distributionId: string | undefined
) => {
  return useApiQuery<Distribution>(
    queryKeys.distributions.detail(distributionId!),
    () => distributionService.getDistribution(fundId!, distributionId!),
    {
      enabled: !!fundId && !!distributionId,
    }
  );
};

/**
 * Hook to fetch distribution notices (PDFs & emails) separately
 */
export const useDistributionNotices = (
  fundId: string | undefined,
  distributionId: string | undefined
) => {
  return useApiQuery<DistributionNotice[]>(
    queryKeys.distributions.notices(distributionId!),
    () => distributionService.getNotices(fundId!, distributionId!),
    {
      enabled: !!fundId && !!distributionId,
    }
  );
};

/**
 * Hook to fetch available asset sales (APPROVED status) for distribution
 */
export const useAvailableAssetSales = (fundId: string | undefined) => {
  return useApiQuery<AvailableAssetSale[]>(
    queryKeys.distributions.availableAssetSales(fundId!),
    () => distributionService.getAvailableAssetSales(fundId!),
    {
      enabled: !!fundId,
    }
  );
};

// Mutation hooks

/**
 * Error type for create-and-finalize partial failures.
 * When create succeeds but finalize fails, we attach the distributionId
 * so the caller can navigate to the detail page for retry.
 */
export interface CreateAndFinalizeError extends APIError {
  phase?: 'create' | 'finalize';
  distributionId?: string;
}

/**
 * Hook to create a distribution and immediately finalize it (generate PDFs + queue emails).
 * Used by the wizard Step 2 flow to skip the manual finalize step.
 */
export const useCreateAndFinalizeDistribution = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    DistributionFinalizeResponse,
    CreateAndFinalizeError,
    { fundId: string; data: DistributionCreateRequest }
  >(
    async ({ fundId, data }) => {
      const created = await distributionService.createDistribution(
        fundId,
        data
      );
      try {
        return await distributionService.finalizeDistribution(
          fundId,
          created.id
        );
      } catch (err) {
        const error = err as CreateAndFinalizeError;
        error.phase = 'finalize';
        error.distributionId = created.id;
        throw error;
      }
    },
    {
      mutationKey: ['createAndFinalizeDistribution'],
      showSuccessToast: false,
      showErrorToast: false,
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.distributions.list(variables.fundId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.documents.lists(),
        });
      },
    }
  );
};

/**
 * Hook to calculate waterfall distribution splits via backend API
 */
export const useCalculateWaterfall = () => {
  return useApiMutation<
    WaterfallCalculationResponse,
    APIError,
    {
      fundId: string;
      distributableAmount: number;
      assetSaleTransactionIds?: string[];
    }
  >(
    ({ fundId, distributableAmount, assetSaleTransactionIds }) =>
      distributionService.calculateWaterfall(
        fundId,
        distributableAmount,
        assetSaleTransactionIds
      ),
    {
      mutationKey: ['calculateWaterfall'],
      showSuccessToast: false,
    }
  );
};

/**
 * Hook to update a distribution
 */
export const useUpdateDistribution = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    Distribution,
    APIError,
    { fundId: string; distributionId: string; data: DistributionUpdateRequest }
  >(
    ({ fundId, distributionId, data }) =>
      distributionService.updateDistribution(fundId, distributionId, data),
    {
      mutationKey: ['updateDistribution'],
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.distributions.list(variables.fundId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.distributions.detail(variables.distributionId),
        });
      },
      successMessage: 'Distribution updated successfully',
    }
  );
};

/**
 * Hook to finalize a distribution (generates PDFs + queues emails).
 *
 * Also invalidates the fund_transactions list so the new ISSUED row
 * (created by the finalize bridge) shows up without a manual refresh.
 */
export const useFinalizeDistribution = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    DistributionFinalizeResponse,
    APIError,
    { fundId: string; distributionId: string }
  >(
    ({ fundId, distributionId }) =>
      distributionService.finalizeDistribution(fundId, distributionId),
    {
      mutationKey: ['finalizeDistribution'],
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.distributions.list(variables.fundId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.distributions.detail(variables.distributionId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.distributions.notices(variables.distributionId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.documents.lists(),
        });
      },
      successMessage: 'Distribution finalized successfully',
    }
  );
};

/**
 * Hook to fetch the FundTransaction wrapper for a finalized distribution.
 *
 * Returns 404 (and `data` stays undefined) before the distribution is finalized.
 * Once available, the FundTransactionDetail powers the reconciliation card on
 * the distribution detail page.
 */
export const useDistributionFundTransaction = (
  fundId: string | undefined,
  distributionId: string | undefined,
  options?: { enabled?: boolean }
) => {
  return useApiQuery<FundTransactionDetail>(
    ['distributionFundTransaction', fundId, distributionId],
    () => distributionService.getFundTransaction(fundId!, distributionId!),
    {
      enabled: !!fundId && !!distributionId && (options?.enabled ?? true),
      retry: false,
    }
  );
};

/**
 * Hook to regenerate distribution notice PDFs
 */
export const useRegenerateNotices = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    DistributionFinalizeResponse,
    APIError,
    { fundId: string; distributionId: string }
  >(
    ({ fundId, distributionId }) =>
      distributionService.regenerateNotices(fundId, distributionId),
    {
      mutationKey: ['regenerateNotices'],
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.distributions.notices(variables.distributionId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.documents.lists(),
        });
      },
      successMessage: 'Distribution notices regenerated',
    }
  );
};

/**
 * Hook to resend distribution notice emails
 */
export const useResendNotices = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    DistributionFinalizeResponse,
    APIError,
    { fundId: string; distributionId: string }
  >(
    ({ fundId, distributionId }) =>
      distributionService.resendNotices(fundId, distributionId),
    {
      mutationKey: ['resendNotices'],
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.distributions.notices(variables.distributionId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.documents.lists(),
        });
      },
      successMessage: 'Distribution emails resent',
    }
  );
};
